import { useQuery } from "@tanstack/react-query";
import { api } from "../singleton/axios";

export const EARNINGS_WEEK_KEYS = ["current", "next"] as const;
export type EarningsWeekKey = (typeof EARNINGS_WEEK_KEYS)[number];

export interface EarningsCalendarEvent {
  symbol: string;
  companyName: string;
  exchange: string;
  currency: string;
  sector: string | null;
  industry: string | null;
  /** In the listing's own currency (see `currency`), major units — what the
   *  card shows. A nightly snapshot, so up to 24h old. */
  marketCap: number | null;
  /** USD-normalised, for the client-side size filter so UK names aren't
   *  judged in pounds. The server already ordered by it. */
  marketCapUsd: number | null;
  callTime: "bmo" | "amc" | null;
}

export interface EarningsCalendarDay {
  date: string; // YYYY-MM-DD
  events: EarningsCalendarEvent[];
}

export interface EarningsCalendarResponse {
  weekStart: string; // YYYY-MM-DD, the ISO Monday
  weekEnd: string;
  /** Free tier: the body carries a trimmed list and the rest is withheld
   *  server-side, so there is nothing hidden in here to reveal. */
  locked: boolean;
  days: EarningsCalendarDay[];
  meta: {
    totalEvents: number;
    hiddenCount: number;
  };
}

interface ApiError {
  success: false;
  error: string;
  message?: string;
}

// A week of earnings entries. The server reads its nightly mirror and makes no
// upstream calls, so this is cheap to poll — but the data only changes once a
// night, so there is nothing to poll for either.
export const useEarningsCalendar = (week: EarningsWeekKey) => {
  return useQuery<EarningsCalendarResponse, ApiError>({
    queryKey: ["earningsCalendar", week],
    queryFn: async () => {
      const { data } = await api.get<EarningsCalendarResponse>(
        "/earnings-calendar",
        {
          params: { week },
        },
      );
      return data;
    },
    staleTime: 5 * 60 * 1000,
    gcTime: 30 * 60 * 1000,
    refetchOnWindowFocus: false,
    // Keeps the previous week on screen while the next one loads — switching
    // weeks should never blank the board.
    placeholderData: (previousData) => previousData,
  });
};
